import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { ErrorBoundary } from '../../../shared/ErrorBoundary.js';
import { useContacts } from '../../contacts/hooks/useContacts.js';
import { useEntityFiles } from '../../contacts/hooks/useContactFiles.js';
import { EntityTasksTab } from '../../contacts/components/ContactTasksTab.js';
import { EntityMeetingsTab } from '../../contacts/components/ContactMeetingsTab.js';
import { EntityNotesTab } from '../../contacts/components/ContactNotesTab.js';
import { EntityDocumentsTab } from '../../contacts/components/ContactDocumentsTab.js';
import { EntityFilesTab } from '../../contacts/components/ContactFilesTab.js';
import { useDeals } from '../../deals/hooks/useDeals.js';
import { useDocuments } from '../../documents/hooks/useDocuments.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { CompanyFormModal } from '../components/CompanyFormModal.js';
import { CompanyDetailHeader } from '../components/CompanyDetailHeader.js';
import { CompanySidebar } from '../components/CompanySidebar.js';
import { CompanyOverview } from '../components/CompanyOverview.js';
import { CompanyContactsTab } from '../components/CompanyContactsTab.js';
import { CompanyDealsTab } from '../components/CompanyDealsTab.js';
import { CompanyHistoryTab } from '../components/CompanyHistoryTab.js';
import { useCompanyDetail } from '../hooks/useCompanyDetail.js';
import { useCompanyHistory } from '../hooks/useCompanyHistory.js';
import '../components/company-detail.css';

export function CompanyDetailPage({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [noteFocus, setNoteFocus] = useState(0);
  const query = useCompanyDetail(companyId);
  // Одни и те же запросы кормят сводку, «Обзор» и счётчики вкладок, а вкладки
  // читают их же ключи — react-query дедуплицирует, лишних запросов нет.
  const contacts = useContacts({ companyId });
  const deals = useDeals({ companyId });
  // Задачи компании — серверное правило наследования (`/tasks?companyId`): свои
  // задачи компании плюс задачи её контактов, сделок и документов, любого
  // статуса. Это же множество считают сводка, «Обзор» и счётчик вкладки.
  const tasks = useTasks({ companyId });
  const notes = useNotes({ companyId });
  const meetings = useMeetings({ companyId });
  const documents = useDocuments({ companyId });
  const files = useEntityFiles('company', companyId);
  const history = useCompanyHistory(companyId);
  useEffect(() => {
    setEditOpen(false);
  }, [companyId]);
  useEffect(() => {
    if (noteFocus && params.get('tab') === 'notes')
      document.querySelector<HTMLTextAreaElement>('[data-testid="entity-note-composer"]')?.focus();
  }, [noteFocus, params]);
  const openTab = (tab: string): void => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
  if (query.isLoading)
    return (
      <main className="company-detail-loading">
        <Skeleton active />
      </main>
    );
  if (query.error || !query.data)
    return (
      <main className="company-detail-loading">
        <ErrorState
          title={t('crm.features.features.companies.pages.companydetailpage.компания_не_найдена')}
          onRetry={() => void query.refetch()}
        />
      </main>
    );
  const company = query.data;
  const contactItems = contacts.data ?? [];
  const contactsCount = contacts.data ? contactItems.length : company.contactsCount;
  const taskItems = tasks.data ?? [];
  const noteItems = notes.data ?? [];
  return (
    <ErrorBoundary>
      <EntityDetailLayout
        header={
          <CompanyDetailHeader
            company={company}
            onEdit={() => setEditOpen(true)}
            onNote={() => {
              openTab('notes');
              setNoteFocus((value) => value + 1);
            }}
            onHistory={() => openTab('history')}
          />
        }
        sidebar={
          <CompanySidebar
            company={company}
            contactsCount={contactsCount}
            deals={deals.deals}
            tasks={taskItems}
            notes={noteItems}
            history={history.items}
          />
        }
        main={
          <EntityTabs
            items={[
              {
                key: 'overview',
                label: t('crm.entity_detail.overview'),
                content: (
                  <CompanyOverview
                    contacts={contactItems}
                    contactsLoading={contacts.isLoading}
                    deals={deals.deals}
                    dealsLoading={deals.isLoading}
                    tasks={taskItems}
                    notes={noteItems}
                    history={history.items}
                  />
                ),
              },
              {
                key: 'contacts',
                label: t('crm.features.company_detail.contacts'),
                count: contactsCount,
                content: (
                  <CompanyContactsTab
                    contacts={contactItems}
                    loading={contacts.isLoading}
                    error={contacts.error}
                  />
                ),
              },
              {
                key: 'deals',
                label: t('crm.entity_detail.deals'),
                count: deals.deals.length,
                content: (
                  <CompanyDealsTab
                    companyId={companyId}
                    deals={deals.deals}
                    loading={deals.isLoading}
                    error={deals.error}
                  />
                ),
              },
              {
                key: 'tasks',
                label: t('crm.entity_detail.tasks'),
                count: taskItems.length,
                tooltip: t('crm.features.company_detail.tasks_hint'),
                content: <EntityTasksTab companyId={companyId} />,
              },
              {
                key: 'meetings',
                label: t('crm.entity_detail.meetings'),
                count: meetings.data?.length ?? 0,
                content: <EntityMeetingsTab companyId={companyId} />,
              },
              {
                key: 'notes',
                label: t('crm.entity_detail.notes'),
                count: noteItems.length,
                content: <EntityNotesTab companyId={companyId} />,
              },
              {
                key: 'documents',
                label: t('crm.entity_detail.documents'),
                count: documents.documents.length,
                content: <EntityDocumentsTab companyId={companyId} showAmount />,
              },
              {
                key: 'files',
                label: t('crm.entity_detail.files'),
                count: files.data?.length ?? 0,
                content: <EntityFilesTab companyId={companyId} />,
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                count: history.items.length,
                content: (
                  <CompanyHistoryTab
                    items={history.items}
                    loading={history.isLoading}
                    error={history.error}
                    onRetry={history.refetch}
                  />
                ),
              },
            ]}
          />
        }
      />
      {editOpen ? (
        <CompanyFormModal open company={company} onClose={() => setEditOpen(false)} />
      ) : null}
    </ErrorBoundary>
  );
}
import { ErrorState } from '../../../shared/ui/ErrorState.js';
